import { Measurement, SHM_VARIABLES } from '@cosmos/shared';
import { SHM_ICORR_THRESHOLD_BANDS } from '../../../measurements/structural-health-measurement/shm-threshold-bands';
import {
  SHM_TEMP_SLOTS, ShmTempSlot, mergeChannelTemps, shmTemperatureTitle,
} from '../../../measurements/structural-health-measurement/shm-series';
import {
  CompareTranslate, CompareTypeAdapter, CompareVariable,
} from './compare-type-adapter.model';

interface ActiveChannel { key: string; type: string; name?: string }

/** Non-'None' channels from either the stored map or the stale Formly array shape. */
function activeChannels(raw: unknown): ActiveChannel[] {
  if (!raw || typeof raw !== 'object') return [];
  const entries: [string, any][] = Array.isArray(raw)
    ? raw.filter((c) => c?.channelId != null).map((c) => [`channel${c.channelId}`, c] as [string, any])
    : Object.entries(raw as Record<string, any>);
  return entries
    .filter(([, c]) => c && c.type && c.type !== 'None')
    .map(([key, c]) => ({ key, type: c.type, name: c.name }));
}

function channelLabel(c: ActiveChannel, t: CompareTranslate): string {
  return c.name?.trim() || `${t('measurement.shm.channel')} ${c.key.replace('channel', '')}`;
}

// `charge` is stored/computed but intentionally not charted (same as the SHM view).
const CORROSION_KEYS = Object.keys(SHM_VARIABLES.corrosion).filter((k) => k !== 'charge');
const CORROSION_DEFS = SHM_VARIABLES.corrosion as Record<string, { label: string; unit: string }>;

export const shmCompareAdapter: CompareTypeAdapter = {
  type: 'StructuralHealthMeasurement',
  // channelDataAll (raw buffer, ~2x payload) is deliberately NOT requested.
  selectFields: ['name', 'measurementType', 'dataChannels', 'tempLabels', 'channelData', 'time'],
  sectionLabelKey: (section) => `measurement.shm.channelTypes.${section}`,

  variables(meta) {
    const channels = activeChannels(meta.dataChannels);
    if (!channels.some((c) => c.type === 'corrosion')) return [];
    const out: CompareVariable[] = CORROSION_KEYS.map((key) => ({
      key,
      labelKey: `measurement.shm.variables.${key}`,
      unit: CORROSION_DEFS[key].unit,
      section: 'corrosion',
      ...(key === 'icorr' ? { thresholdBands: SHM_ICORR_THRESHOLD_BANDS } : {}),
    }));
    for (const slot of SHM_TEMP_SLOTS) {
      out.push({ key: slot, labelKey: `measurement.shm.variables.${slot}`, unit: 'ºC', section: 'temperature' });
    }
    return out;
  },

  filters(meta, t) {
    const channels = activeChannels(meta.dataChannels);
    if (channels.length < 2) return [];
    return [{
      id: 'channels',
      labelKey: 'measurement.shm.channelFilter',
      mode: 'multi',
      options: channels.map((c) => ({ value: c.key, label: channelLabel(c, t) })),
    }];
  },

  buildSeries(m: Measurement, variable, filterState, t) {
    const channels = activeChannels((m as any).dataChannels).filter((c) => c.type === 'corrosion');
    // Stale/empty filter entries (channels that no longer exist) fall back to all channels.
    const chosen = (filterState['channels'] ?? []).filter((k) => channels.some((c) => c.key === k));
    const selected = chosen.length ? channels.filter((c) => chosen.includes(c.key)) : channels;

    const channelData = ((m as any).channelData ?? {}) as Record<string, Record<string, any[]>>;
    const fallbackTime: any[] = (m as any).time ?? [];

    if (variable.section === 'temperature') {
      const inputs = selected.map((c) => ({
        channelKey: c.key,
        ...channelData[c.key],
        time: channelData[c.key]?.['time'] ?? fallbackTime,
      }));
      const merged = mergeChannelTemps(inputs, variable.key);
      if (!merged) return [];
      const title = shmTemperatureTitle(variable.key as ShmTempSlot, (m as any).tempLabels, t);
      return [{ label: `${m.name} · ${title}`, time: merged.time, values: merged.values }];
    }

    return selected.flatMap((c) => {
      const cd = channelData[c.key];
      const values = cd?.[variable.key];
      if (!Array.isArray(values) || !values.length) return [];
      const timeSrc = variable.key === 'ecorr' ? (cd['time_ecorr'] ?? cd['time']) : cd['time'];
      const time = (timeSrc ?? fallbackTime).map((x: any) => new Date(x));
      return [{
        label: `${m.name} · ${channelLabel(c, t)}`,
        time,
        values,
        meta: { measurementId: m._id as string, channelKey: c.key },
      }];
    });
  },
};
